import { useEffect } from "react";
import "./CalculadoraBCV.scss";
import {
  CalculatorIcon,
  CloseIcon,
  CalendarIcon,
  CoinIcon,
  CopyIcon,
  CheckIcon,
} from "./icons";

/**
 * Fecha de hoy en formato ISO AAAA-MM-DD, para limitar el selector de
 * fecha e impedir seleccionar días futuros (sin tasa disponible).
 *
 * @returns {string} Fecha local (ej. "2026-06-09").
 */
const getTodayISO = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Modal de presentación de la Calculadora BCV. Componente PURO:
 * toda la lógica entra por props desde `useCurrencyConverter`.
 */
export const CalculadoraBCVModal = (props) => {
  const {
    isOpen,
    onClose,
    paymentDate,
    onPaymentDateChange,
    rateFormatted,
    isLoadingRate,
    usdAmount,
    onUsdAmountChange,
    bsAmountFormatted,
    onCopy,
    copied,
  } = props;

  // Cierra con la tecla Escape mientras la modal está abierta.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="bcv-overlay" onMouseDown={onClose}>
      <div
        className="bcv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bcv-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        {/* Encabezado */}
        <div className="bcv-modal__header">
          <span className="bcv-modal__header-icon">
            <CalculatorIcon />
          </span>
          <div className="bcv-modal__header-text">
            <h2 id="bcv-title" className="bcv-modal__title">
              Calculadora BCV
            </h2>
            <p className="bcv-modal__subtitle">
              Conversión USD → Bs. según tasa del día
            </p>
          </div>
          <button
            type="button"
            className="bcv-modal__close"
            onClick={onClose}
            aria-label="Cerrar calculadora"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="bcv-modal__body">
          {/* Fecha del pago */}
          <label className="bcv-field">
            <span className="bcv-field__label">Fecha del pago</span>
            <div className="bcv-input bcv-input--editable">
              <span className="bcv-input__icon">
                <CalendarIcon />
              </span>
              <input
                type="date"
                className="bcv-input__control"
                value={paymentDate}
                onChange={onPaymentDateChange}
                max={getTodayISO()}
                aria-label="Fecha del pago"
              />
            </div>
          </label>

          {/* Tasa + Monto USD */}
          <div className="bcv-row">
            <label className="bcv-field">
              <span className="bcv-field__label">Tasa BCV (Bs/USD)</span>
              <div className="bcv-input bcv-input--readonly">
                <span className="bcv-input__icon">
                  <CoinIcon />
                </span>
                <input
                  type="text"
                  className="bcv-input__control"
                  value={isLoadingRate ? "Cargando..." : rateFormatted}
                  readOnly
                  aria-label="Tasa BCV en bolívares por dólar"
                />
              </div>
            </label>

            <label className="bcv-field">
              <span className="bcv-field__label">Monto en USD ($)</span>
              <div className="bcv-input bcv-input--editable">
                <span className="bcv-input__symbol">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="bcv-input__control"
                  value={usdAmount}
                  onChange={onUsdAmountChange}
                  aria-label="Monto en dólares"
                />
                <span className="bcv-input__suffix">USD</span>
              </div>
            </label>
          </div>

          {/* Resultado */}
          <div className="bcv-result">
            <div className="bcv-result__info">
              <span className="bcv-result__label">Monto en Bolívares</span>
              <span className="bcv-result__amount">Bs. {bsAmountFormatted}</span>
            </div>
            <button
              type="button"
              className={`bcv-result__copy${copied ? " is-copied" : ""}`}
              onClick={onCopy}
              aria-label="Copiar monto en bolívares"
            >
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied && <span className="bcv-result__tooltip">¡Copiado!</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
